import { useId } from 'react';
import { AdminSelect } from '../../components/primitives/admin-select.js';
import type { AdminBrand, AdminCategory, AdminTaxRate } from '../types/catalog.js';
import type { ProductFormState } from '../pages/product-editor-page.js';

export interface ProductGeneralTabProps {
  form: ProductFormState;
  onChange: (patch: Partial<ProductFormState>) => void;
  categories: AdminCategory[];
  brands: AdminBrand[];
  taxRates: AdminTaxRate[];
  errors: Partial<Record<keyof ProductFormState, string>>;
}

export function ProductGeneralTab({ form, onChange, categories, brands, taxRates, errors }: ProductGeneralTabProps) {
  const nameId = useId();
  const slugId = useId();
  const descriptionId = useId();

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor={nameId} className="mb-1 block text-sm font-medium text-gray-700">
          Name
        </label>
        <input
          id={nameId}
          value={form.name}
          onChange={(event) => onChange({ name: event.target.value })}
          aria-invalid={Boolean(errors.name)}
          aria-describedby={errors.name ? `${nameId}-error` : undefined}
          className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2"
        />
        {errors.name && (
          <p id={`${nameId}-error`} className="mt-1 text-xs text-red-600">
            {errors.name}
          </p>
        )}
      </div>

      <div>
        <label htmlFor={slugId} className="mb-1 block text-sm font-medium text-gray-700">
          Slug <span className="font-normal text-gray-400">(leave blank to auto-generate)</span>
        </label>
        <input
          id={slugId}
          value={form.slug}
          onChange={(event) => onChange({ slug: event.target.value })}
          aria-invalid={Boolean(errors.slug)}
          className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2"
        />
        {errors.slug && <p className="mt-1 text-xs text-red-600">{errors.slug}</p>}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <AdminSelect
          label="Category"
          value={form.categoryId}
          onChange={(value) => onChange({ categoryId: value })}
          options={categories.map((c) => ({ value: c.id, label: c.name }))}
        />
        <AdminSelect
          label="Brand"
          value={form.brandId}
          onChange={(value) => onChange({ brandId: value })}
          allLabel="No brand"
          options={brands.map((b) => ({ value: b.id, label: b.name }))}
        />
        <AdminSelect
          label="Tax rate"
          value={form.taxRateId}
          onChange={(value) => onChange({ taxRateId: value })}
          options={taxRates.map((t) => ({ value: t.id, label: `${t.name} (${t.rateBps / 100}%)` }))}
        />
      </div>
      {errors.categoryId && <p className="text-xs text-red-600">{errors.categoryId}</p>}
      {errors.taxRateId && <p className="text-xs text-red-600">{errors.taxRateId}</p>}

      <div>
        <label htmlFor={descriptionId} className="mb-1 block text-sm font-medium text-gray-700">
          Description
        </label>
        <textarea
          id={descriptionId}
          value={form.description}
          onChange={(event) => onChange({ description: event.target.value })}
          rows={5}
          className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2"
        />
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={form.isFeatured}
          onChange={(event) => onChange({ isFeatured: event.target.checked })}
          className="rounded border-gray-300"
        />
        Featured product
      </label>
    </div>
  );
}
